import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, ArrowRightIcon, ClockIcon, XIcon } from 'lucide-react';
import { useBusiness } from '../BusinessContext';
import { useSetupProgress } from '../../hooks/useSetupProgress';
import { HubTarget } from '../../data/hubSetup';
interface SetupGuideProps {
  catalogLabel: string;
  onNavigate: (tab: string) => void;
}

/**
 * The first thing a new business sees in the hub: not a dashboard of zeros,
 * but the path from signing up to a first customer conversation. Only the
 * next step is open, so there is always exactly one obvious thing to do;
 * finished steps collapse to a tick and later ones stay quiet until reached.
 */
export function SetupGuide({
  catalogLabel,
  onNavigate
}: SetupGuideProps) {
  const {
    hideSetup,
    hub
  } = useBusiness();
  const {
    steps,
    isDone,
    doneCount,
    total,
    next,
    complete,
    hidden
  } = useSetupProgress();
  if (hidden) return null;
  const go = (target: HubTarget) => onNavigate(target === 'catalogue' ? catalogLabel : target);
  const pct = Math.round(doneCount / total * 100);
  return <section aria-labelledby="setup-guide-title" className="bg-white rounded-3xl border border-slate-200/70 shadow-sm overflow-hidden" data-id="element-2292" data-mp="2xy0hrs0rdv2d">
      <div className="px-6 lg:px-8 pt-6 pb-5 flex items-start justify-between gap-6" data-id="element-2293" data-mp="3br00e2vu85e5">
        <div className="min-w-0" data-id="element-2294" data-mp="36wvpx9nd5f8b">
          <h2 id="setup-guide-title" className="font-display font-bold text-xl text-dark-navy" data-id="element-2295" data-mp="3ln32j8qow413">
            {complete ? `${hub?.name ?? 'Your business'} is ready for customers` : 'Get your business ready for customers'}
          </h2>
          <p className="text-sm text-slate-500 mt-1" data-id="element-2296" data-mp="3qm642j7gns4b">
            {complete ? 'Every step is done. Your shop is live and new conversations will show up in Leads.' : 'Five short steps, in order. Each one takes you to the part of the hub that handles it.'}
          </p>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0" data-id="element-2297" data-mp="3vd51hj768t2p">
          <div className="hidden sm:block text-right" data-id="element-2298" data-mp="3vezmexig0l6s">
            <p className="text-sm font-bold text-dark-navy tabular-nums" data-id="element-2299" data-mp="47n1q8icppm4">
              {doneCount} of {total}
            </p>
            <div className="w-28 h-1.5 rounded-full bg-slate-100 mt-1.5 overflow-hidden" data-id="element-2300" data-mp="943szbkukk3f">
              <motion.div className="h-full rounded-full bg-primary-indigo" initial={false} animate={{
              width: `${pct}%`
            }} transition={{
              duration: 0.3,
              ease: [0.23, 1, 0.32, 1]
            }} data-id="element-2301" data-mp="e6clk6tt5k50" />
            </div>
          </div>
          {complete && <button onClick={hideSetup} aria-label="Hide setup guide" className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-50 hover:text-dark-navy transition-colors duration-150 ease-out" data-id="element-2302" data-mp="wupuek0cj64g">
              <XIcon className="w-4 h-4" data-id="element-2303" data-mp="2qerjnikan50j" />
            </button>}
        </div>
      </div>

      <ol className="border-t border-slate-100" data-id="element-2304" data-mp="2mbliuqfya61g">
        {steps.map((step, i) => {
        const done = isDone(step.id);
        const current = next?.id === step.id;
        return <li key={step.id} className={`border-b border-slate-100 last:border-b-0 ${current ? 'bg-primary-indigo/[0.025]' : ''}`} data-id="element-2305" data-mp="2cf8mtlwxng3j" data-mpi={i}>
              <div className="px-6 lg:px-8 py-4 flex items-start gap-4" data-id="element-2306" data-mp="352copi0qbeb3">
                <span className={`mt-0.5 w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-[12px] font-bold ${done ? 'bg-success text-white' : current ? 'bg-primary-indigo text-white' : 'bg-slate-100 text-slate-400'}`} aria-hidden="true" data-id="element-2307" data-mp="30c619lhydzuf">
                  {done ? <CheckIcon className="w-3.5 h-3.5" data-id="element-2308" data-mp="3o18f0i9gv8jn" /> : i + 1}
                </span>

                <div className="flex-1 min-w-0" data-id="element-2309" data-mp="38jz162cndgfm">
                  <div className="flex items-center justify-between gap-4" data-id="element-2310" data-mp="38i5tqku7ms6k">
                    <p className={`font-semibold text-[15px] ${done ? 'text-slate-400' : 'text-dark-navy'}`} data-id="element-2311" data-mp="3cmdiuj4kya5b">
                      {step.title}
                      <span className="sr-only" data-id="element-2312" data-mp="33juj47w3cdna">
                        {done ? ' — done' : current ? ' — next step' : ''}
                      </span>
                    </p>
                    {!current && <button onClick={() => go(step.target)} className="text-[13px] font-semibold text-slate-400 hover:text-primary-indigo transition-colors duration-150 ease-out flex-shrink-0" data-id="element-2313" data-mp="2vv5mpj453j">
                        {done ? 'Review' : 'Open'}
                      </button>}
                  </div>

                  <AnimatePresence initial={false} data-id="element-2314" data-mp="ivgk4t23xq22">
                    {current && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.22,
                  ease: [0.23, 1, 0.32, 1]
                }} className="overflow-hidden" data-id="element-2315" data-mp="23k0son6kstkv">
                        <p className="text-sm text-slate-600 mt-1.5 max-w-2xl" data-id="element-2316" data-mp="1ont7bjjflm40">
                          {step.what}
                        </p>
                        <p className="text-[13px] text-slate-400 mt-1 max-w-2xl" data-id="element-2317" data-mp="3508aqzcejiqr">
                          {step.why}
                        </p>
                        <div className="flex items-center gap-4 mt-4" data-id="element-2318" data-mp="1xpkdd9s0bowt">
                          <button onClick={() => go(step.target)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-indigo text-white text-sm font-semibold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-2319" data-mp="1xreojwdh82np">
                            {step.cta}
                            <ArrowRightIcon className="w-4 h-4" data-id="element-2320" data-mp="1f03ytijhlvxe" />
                          </button>
                          <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-400" data-id="element-2321" data-mp="1j17dw2tpnnxk">
                            <ClockIcon className="w-3.5 h-3.5" data-id="element-2322" data-mp="1nypglbh4x6x4" />
                            About {step.minutes} min
                          </span>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </div>
            </li>;
      })}
      </ol>
    </section>;
}